// Catalog.ID Client — OIDC Consent Screen
// ============================================================================
// Renders the OIDC authorization consent screen. ONE renderer
// (`renderConsentScreen`) serves both live entry points, because an auth screen
// that exists twice is an auth screen whose two copies drift:
//
//   - `showOidcConsentModal` — hub-relayed requests (desktop cid-login,
//     publisher, work), opened by key-grant/client.ts. Approve/deny travel back
//     over the hub through the pending-consent store.
//   - `renderOidcConsentStandalone` — the browser route `/oidc/consent`
//     (app.ts), where the server has redirected the user's browser. Nothing is
//     waiting on the relay, so delivery is the ordinary RP redirect/form POST.
//
// The two differ only in what the HOST can do, which is what `ConsentUi`
// carries: the modal has a shell (toast, close), the standalone route has
// neither and shows errors inline in its own card. Until 2026-09-10 the browser
// route had a whole second renderer whose approve/deny handlers were `catch {}`
// — a failed authorization said nothing at all.
//
// There was also a `createOidcConsentPane` pane type, registered but never
// opened by anything; it was removed with the duplicate renderer.

import { createElement, createFieldGroup, createFormRow, createEmptyState } from '@catalog-shell';
import type { Shell } from '@catalog-shell';
import { claimTypeLabel } from './claims-list-pane';
import * as oidcApi from '../api/oidc';
import * as claimsApi from '../api/claims';
import { decryptClaim } from '../crypto/claims';
import * as aes from '../crypto/aes';
import { keys } from '../state/keys';
import { signStepUp } from '../auth/step-up';
import { resolveRelayConsent } from '../key-grant/pending-consent';
import type { OidcAuthorizationDetails, OidcApproveResponse } from '../api/types';
import { session } from '../state/session';
import { identity } from '../state/identity';
import * as deviceStorage from '../state/device-storage';
import { getStoredOrigin, originIsElsewhere } from '../state/origins';
import { getApiBase } from '../state/dashboard-context';
import * as accountApi from '../api/account';
import { renderLoginView, renderDeviceUnlockView } from '../auth/login-view';

/** Base64url encode (no padding) per RFC 7515 */
function base64url(bytes: Uint8Array): string {
    let binary = '';
    for (const b of bytes) binary += String.fromCharCode(b);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

interface SdJwtResult {
    hashes: string[];
    claimNames: string[];
    /** The actual base64url-encoded disclosures to deliver to the RP. */
    disclosures: string[];
}

/** Compute SD-JWT disclosure hashes per IETF draft-ietf-oauth-selective-disclosure-jwt.
 *  For each claim: generate random salt, build disclosure = base64url(JSON([salt, name, value])),
 *  compute hash = base64url(SHA-256(UTF-8(disclosure))).
 *  Returns both hashes (sent to server) and disclosures (delivered to RP via form POST). */
async function computeSdJwtHashes(
    claimUuids: Map<string, string>, // claimType → claimUuid
): Promise<SdJwtResult | null> {
    const attrKey = keys.getPrivateKey('attr_encrypt');
    if (!attrKey) return null;

    const hashes: string[] = [];
    const claimNames: string[] = [];
    const disclosures: string[] = [];

    for (const [claimType, claimUuid] of claimUuids) {
        try {
            const detail = await claimsApi.getClaim(claimUuid);
            const selfKey = detail.keys.find(k => k.recipientType === 'self');
            if (!selfKey) continue;

            const decrypted = await decryptClaim(
                detail.encryptedValue, detail.nonce, selfKey.encryptedKey, aes.fromBase64(attrKey),
            );

            // Generate random salt (16 bytes, base64url-encoded)
            const salt = crypto.getRandomValues(new Uint8Array(16));
            const saltB64url = base64url(salt);

            // Build IETF SD-JWT disclosure: base64url(JSON([salt, claim_name, value]))
            const disclosureJson = JSON.stringify([saltB64url, claimType, decrypted]);
            const disclosureBytes = new TextEncoder().encode(disclosureJson);
            const disclosure = base64url(disclosureBytes);

            // Compute hash: base64url(SHA-256(UTF-8(disclosure)))
            const hashBytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(disclosure));
            hashes.push(base64url(new Uint8Array(hashBytes)));
            claimNames.push(claimType);
            disclosures.push(disclosure);
        } catch {
            // Skip claims that can't be decrypted
        }
    }

    return hashes.length > 0 ? { hashes, claimNames, disclosures } : null;
}

/**
 * The parts of a pending authorization the silent-consent path checks
 * (`key-grant/desktop-handlers.ts`), read from the node — never from the
 * envelope that asked.
 */
export async function fetchAuthorizationSummary(
    requestId: string,
): Promise<{ clientId: string; scopes: string[]; claimsRequested: boolean }> {
    const details = await oidcApi.getAuthorizationDetails(requestId);
    const claims = details.requestedClaims;
    return {
        clientId: details.relyingParty.clientId,
        scopes: details.requestedScopes,
        claimsRequested: !!claims && (claims.essential.length > 0 || claims.optional.length > 0),
    };
}

/**
 * Approve without the dialog — what the dialog's Approve does for an
 * authorization with no private claims: the same step-up proof, the requested
 * scopes, no claim disclosures. Only ever reached through the silent-consent
 * gate (the desktop's own client, after this tab's own login).
 */
export async function approveWithoutDialog(
    requestId: string, scopes: string[],
): Promise<{ code: string; state: string; iss: string }> {
    const challengeSignature = await signStepUp('oidc-approve');
    const resp = await oidcApi.approveAuthorization(requestId, {
        consentedScopes: scopes,
        consentedClaimTypes: null,
        sdJwt: null,
        challengeSignature,
    });
    return { code: resp.code, state: resp.state, iss: resp.iss };
}

/**
 * Show the consent screen as a modal dialog over the whole CID module. This is
 * how hub-relayed requests (desktop cid-login) present consent — a dialog the
 * user cannot miss, rather than a pane competing with the rest of the layout.
 */
export function showOidcConsentModal(shell: Shell, requestId: string): void {
    const content = createElement('div', { className: 'cs-entity-view cs-modal-body' });
    content.appendChild(createElement('h2', {
        className: 'cs-modal-title',
        textContent: 'Authorization Request',
    }));
    const body = createElement('div');
    content.appendChild(body);

    const handle = shell.showModal(content);
    void renderConsentScreen(body, requestId, {
        showError: (message) => shell.toast(message, 'error'),
        closeAfterRelay: () => handle.close(),
    });
}

/**
 * Host-specific bits of the consent screen — everything the renderer itself
 * cannot decide.
 */
export interface ConsentUi {
    /** Surface a failed approve/deny. The modal toasts; the standalone card
     *  writes an inline line, having no shell to toast with. */
    showError: (message: string) => void;
    /** Dismiss the surface once a hub-relayed result has been delivered. Never
     *  called on the browser route: nothing is registered in the pending-consent
     *  store there, so `resolveRelayConsent` misses and returns false. */
    closeAfterRelay: () => void;
    /**
     * Render the "you must be logged in" branch, if this host has one. Only the
     * browser route does — it can send the visitor to the login page and back —
     * so the check itself is skipped when the hook is absent, leaving the modal
     * flow exactly as it was.
     */
    onNotSignedIn?: (requestId: string) => HTMLElement | void;
    /**
     * Send the browser to the relying party. Defaults to a top-level navigation;
     * tests capture it. Never used for a hub-relayed result.
     */
    navigate?: (url: string) => void;
}

/** Top-level navigation: how a browser OIDC flow moves between parties. */
function defaultNavigate(url: string): void {
    window.location.assign(url);
}

async function renderConsentScreen(
    container: HTMLElement,
    requestId: string,
    ui: ConsentUi,
): Promise<void> {
    container.appendChild(createElement('p', { className: 'cid-wizard-status', textContent: 'Loading authorization request...' }));

    let details: OidcAuthorizationDetails;
    try {
        details = await oidcApi.getAuthorizationDetails(requestId);
    } catch {
        container.innerHTML = '';
        container.appendChild(createEmptyState({
            message: 'Request Not Found',
            description: 'This authorization request has expired or does not exist.',
        }));
        return;
    }

    container.innerHTML = '';

    // Hosts that can do something about a missing session get to; the modal
    // cannot (it opens over an unlocked identity in the first place) and so
    // supplies no hook and keeps its pre-2026-09-10 behaviour.
    if (ui.onNotSignedIn && !details.isAuthenticated) {
        const branch = ui.onNotSignedIn(requestId);
        if (branch) container.appendChild(branch);
        return;
    }

    // RP info
    const rpGroup = createFieldGroup({ title: 'Application Requesting Access' });

    const nameRow = createFormRow({ label: 'Application' });
    nameRow.content.appendChild(createElement('span', { textContent: details.relyingParty.displayName }));
    rpGroup.body.appendChild(nameRow.element);

    if (details.relyingParty.displayDomain) {
        const domainRow = createFormRow({ label: 'Domain' });
        domainRow.content.appendChild(createElement('span', { textContent: details.relyingParty.displayDomain }));
        rpGroup.body.appendChild(domainRow.element);
    }

    container.appendChild(rpGroup.element);

    // Scopes
    const scopeGroup = createFieldGroup({ title: 'Requested Permissions' });
    for (const scope of details.requestedScopes) {
        const row = createFormRow({ label: scope });
        row.content.appendChild(createElement('span', {
            textContent: SCOPE_DESCRIPTIONS[scope] ?? scope,
        }));
        scopeGroup.body.appendChild(row.element);
    }
    container.appendChild(scopeGroup.element);

    // Public claims (shown for transparency, not toggleable)
    if (details.memberClaims.publicClaims.length > 0) {
        const pubGroup = createFieldGroup({ title: 'Public Claims (already published)' });
        for (const claim of details.memberClaims.publicClaims) {
            const row = createFormRow({ label: claimTypeLabel(claim.type) });
            row.content.appendChild(createElement('span', { textContent: claim.value }));
            pubGroup.body.appendChild(row.element);
        }
        container.appendChild(pubGroup.element);
    }

    // Private claims (checkboxes)
    const consentedClaimTypes = new Set<string>();
    const claimUuidMap = new Map<string, string>(); // claimType → claimUuid for SD-JWT
    const hasRequestedClaims = details.requestedClaims &&
        (details.requestedClaims.essential.length > 0 || details.requestedClaims.optional.length > 0);

    if (hasRequestedClaims && details.memberClaims.privateClaims.length > 0) {
        const privGroup = createFieldGroup({ title: 'Private Claims (your consent required)' });

        const essential = new Set(details.requestedClaims!.essential);
        const optional = new Set(details.requestedClaims!.optional);

        for (const claim of details.memberClaims.privateClaims) {
            const isEssential = essential.has(claim.type);
            const isOptional = optional.has(claim.type);
            if (!isEssential && !isOptional) continue;

            const row = createElement('div', { className: 'cid-auth-checkbox-row' });
            const cb = document.createElement('input');
            cb.type = 'checkbox';
            cb.id = `consent-claim-${claim.type}`;

            if (isEssential) {
                cb.checked = true;
                consentedClaimTypes.add(claim.type);
                claimUuidMap.set(claim.type, claim.claimUuid);
                cb.addEventListener('change', () => {
                    // Essential claims must stay checked
                    cb.checked = true;
                });
            } else {
                cb.addEventListener('change', () => {
                    if (cb.checked) {
                        consentedClaimTypes.add(claim.type);
                        claimUuidMap.set(claim.type, claim.claimUuid);
                    } else {
                        consentedClaimTypes.delete(claim.type);
                        claimUuidMap.delete(claim.type);
                    }
                });
            }

            const label = createElement('label');
            label.setAttribute('for', cb.id);
            const labelText = claimTypeLabel(claim.type);
            const suffix = isEssential ? ' (required)' : '';
            const validated = claim.validated ? ' — validated' : ' — not validated';
            label.textContent = `${labelText}${suffix}${validated}`;

            row.appendChild(cb);
            row.appendChild(label);
            privGroup.body.appendChild(row);
        }

        container.appendChild(privGroup.element);
    }

    // Action buttons
    const actionGroup = createFieldGroup({ title: '' });
    const btnRow = createElement('div', { className: 'cid-pane-row' });

    const approveBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes',
        textContent: 'Approve',
    });
    approveBtn.addEventListener('click', async () => {
        approveBtn.setAttribute('disabled', '');
        denyBtn.setAttribute('disabled', '');
        approveBtn.textContent = 'Authorizing...';

        try {
            // Compute SD-JWT hashes for consented private claims
            let sdJwt: SdJwtResult | null = null;
            if (claimUuidMap.size > 0) {
                approveBtn.textContent = 'Decrypting claims...';
                sdJwt = await computeSdJwtHashes(claimUuidMap);
            }

            approveBtn.textContent = 'Authorizing...';
            const challengeSignature = await signStepUp('oidc-approve');
            const resp = await oidcApi.approveAuthorization(requestId, {
                consentedScopes: details.requestedScopes,
                consentedClaimTypes: consentedClaimTypes.size > 0
                    ? Array.from(consentedClaimTypes)
                    : null,
                sdJwt,
                challengeSignature,
            });

            // Hub-relayed request (desktop cid-login): the code returns to the
            // desktop service through the module hub — no browser redirect. The
            // issuer travels with it: the module exchanges the code there only.
            if (resolveRelayConsent(requestId, {
                outcome: 'approved', code: resp.code, state: resp.state, iss: resp.iss,
            })) {
                ui.closeAfterRelay();
                return;
            }

            deliverToRp(resp, sdJwt?.disclosures, ui.navigate ?? defaultNavigate);
        } catch (err) {
            approveBtn.removeAttribute('disabled');
            denyBtn.removeAttribute('disabled');
            approveBtn.textContent = 'Approve';
            ui.showError(err instanceof Error ? err.message : 'Authorization failed');
        }
    });

    const denyBtn = createElement('button', {
        className: 'cs-btn cs-btn--no',
        textContent: 'Deny',
    });
    denyBtn.addEventListener('click', async () => {
        approveBtn.setAttribute('disabled', '');
        denyBtn.setAttribute('disabled', '');

        try {
            const resp = await oidcApi.denyAuthorization(requestId);
            if (resolveRelayConsent(requestId, { outcome: 'denied' })) {
                ui.closeAfterRelay();
                return;
            }
            (ui.navigate ?? defaultNavigate)(rpResponseUrl(resp.redirectUri, {
                error: resp.error, state: resp.state, iss: resp.iss,
            }));
        } catch (err) {
            approveBtn.removeAttribute('disabled');
            denyBtn.removeAttribute('disabled');
            ui.showError(err instanceof Error ? err.message : 'Failed to deny');
        }
    });

    btnRow.appendChild(approveBtn);
    btnRow.appendChild(denyBtn);
    actionGroup.body.appendChild(btnRow);
    container.appendChild(actionGroup.element);
}

/**
 * The relying party's redirect URI with an authorization response's parameters
 * added — its own query kept. Every response names its issuer (`iss`, RFC 9207,
 * docs/cid-governance.md M7b): a flow may have been forwarded from the node the
 * relying party chose to the account's origin, and the relying party must
 * exchange the code where it was minted.
 */
export function rpResponseUrl(redirectUri: string, params: Record<string, string>): string {
    const url = new URL(redirectUri);
    for (const [name, value] of Object.entries(params)) url.searchParams.set(name, value);
    return url.toString();
}

/** Redirect to the RP with the authorization code, or deliver via form POST if disclosures are present. */
function deliverToRp(
    resp: OidcApproveResponse,
    disclosures: string[] | undefined,
    navigate: (url: string) => void,
): void {
    if (resp.deliveryMode === 'form_post' && disclosures && disclosures.length > 0) {
        // SD-JWT form POST: deliver code + disclosures via a hidden form submission.
        // The disclosures contain the actual claim values — they never touch the server.
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = resp.redirectUri;
        form.style.display = 'none';

        const addField = (name: string, value: string) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
        };

        addField('code', resp.code);
        addField('state', resp.state);
        addField('iss', resp.iss);
        for (const d of disclosures) {
            addField('disclosure', d);
        }

        document.body.appendChild(form);
        form.submit();
    } else {
        // Standard redirect with code, state and issuer in the query
        navigate(rpResponseUrl(resp.redirectUri, { code: resp.code, state: resp.state, iss: resp.iss }));
    }
}

const SCOPE_DESCRIPTIONS: Record<string, string> = {
    openid: 'Verify your identity (CID)',
    profile: 'Access your public profile (published claims)',
    email: 'Access your email address',
    phone: 'Access your phone number',
    claims: 'Access selected private claims',
};

// ============================================================================
// Standalone consent screen (browser route /oidc/consent?request_id=...)
//
// The SERVER redirected the browser here (OidcAuthorizeController), so there is
// no shell and no relay: errors go inline into the card, and delivery is the
// ordinary RP redirect / form POST. The card and its host adapter are all this
// route adds — the screen itself is `renderConsentScreen`.
// ============================================================================

export function renderOidcConsentStandalone(
    container: HTMLElement, requestId: string, options: { navigate?: (url: string) => void } = {},
): void {
    container.innerHTML = '';

    const wrapper = createElement('div', { className: 'cid-auth-container' });
    const card = createElement('div', { className: 'cid-auth-card' });
    card.style.width = '520px';

    const title = createElement('h2', { className: 'cid-auth-title', textContent: 'Authorization Request' });
    card.appendChild(title);

    const contentEl = createElement('div');
    card.appendChild(contentEl);
    wrapper.appendChild(card);
    container.appendChild(wrapper);

    void renderConsentScreen(contentEl, requestId, { ...standaloneConsentUi(card), navigate: options.navigate });
}

/**
 * The browser route's host adapter. Exported for its own test: this is the half
 * of the consent screen that differs between the two entry points.
 */
export function standaloneConsentUi(card: HTMLElement): ConsentUi {
    let errorLine: HTMLElement | null = null;
    return {
        showError: (message) => {
            // One line, reused — a failed approve can be retried, and stacking
            // a line per attempt would push the buttons off the card.
            if (!errorLine) {
                errorLine = createElement('p', { className: 'cid-pane-error cid-pane-error--visible' });
                card.appendChild(errorLine);
            }
            errorLine.textContent = message;
        },
        closeAfterRelay: () => {
            // Unreachable on this route: no pending-consent entry exists, so
            // `resolveRelayConsent` returns false and the renderer delivers to
            // the RP instead of calling this.
        },
        onNotSignedIn: (rid) => {
            const branch = createElement('div');
            branch.appendChild(createElement('p', {
                className: 'cid-wizard-warning',
                textContent: 'You must be logged in to authorize this request.',
            }));
            const loginBtn = createElement('a', {
                className: 'cs-btn cs-btn--yes',
                textContent: 'Log In',
            });
            loginBtn.setAttribute('href', `/?redirect=/oidc/consent?request_id=${rid}`);
            branch.appendChild(loginBtn);
            return branch;
        },
    };
}

// ============================================================================
// The browser route /oidc/consent?request_id=…[&login_hint=…]
// (docs/cid-governance.md D18, M7a, M7b)
//
// The request id lives in the memory of the node that served this page — the
// ENTRY node. Consent, the code and the tokens belong to the account's ORIGIN.
// When the identity signing in lives elsewhere, the page does not sign in here
// at all: it asks the entry node to forward the authorization, and the entry
// node answers 302 to the origin's /oidc/authorize (target from its node
// directory, never from this page), where the origin mints its own request and
// shows its own consent page. The one place the CID client navigates top-level
// to another node, because a browser OIDC flow is the one flow that belongs to
// the browser rather than to this client.
// ============================================================================

/** The entry node's forward of `requestId` to `cid`'s origin. */
export function consentForwardUrl(requestId: string, cid: string): string {
    return `${getApiBase()}/api/oidc/authorize/${encodeURIComponent(requestId)}/forward`
        + `?login_hint=${encodeURIComponent(cid)}`;
}

export interface OidcConsentRouteParams {
    requestId: string;
    /** The identity the relying party (or a forward) named, to prefill the login. */
    loginHint?: string;
}

export interface OidcConsentRouteHooks {
    onRegister: () => void;
    /** Top-level navigation; tests capture it. */
    navigate?: (url: string) => void;
}

/** Whether `cid` is known to live at another node than this page's. */
function originElsewhere(cid: string): boolean {
    const origin = getStoredOrigin(cid);
    return origin !== null && originIsElsewhere(origin);
}

export function renderOidcConsentRoute(
    appEl: HTMLElement, params: OidcConsentRouteParams, hooks: OidcConsentRouteHooks,
): void {
    const navigate = hooks.navigate ?? defaultNavigate;
    const { requestId } = params;
    const forward = (cid: string) => navigate(consentForwardUrl(requestId, cid));
    // After a sign-in or an unlock the identity's origin may have become known
    // (an `origin_only` answer the client followed): check again before
    // asking this node about a request only it holds.
    const consent = () => {
        const cid = deviceStorage.getActiveCid();
        if (cid && originElsewhere(cid)) forward(cid);
        else renderOidcConsentStandalone(appEl, requestId, { navigate: hooks.navigate });
    };

    if (session.isActive()) {
        const cid = deviceStorage.getActiveCid();
        // Signed in here as an identity of another node: forward before anything
        // else — an unlock here would unlock the wrong page's device.
        if (cid && originElsewhere(cid)) {
            forward(cid);
            return;
        }
        if (keys.isLoaded()) {
            consent();
            return;
        }
        // Session exists but keys not loaded — device unlock first
        renderDeviceUnlockView(appEl, {
            onSuccess: async () => {
                try {
                    const info = await accountApi.getAccountInfo();
                    session.updateInfo({ cid: info.cid, accountType: info.accountType, status: info.status, isOperator: info.isOperator, sessionId: info.sessionId });
                    identity.setActive({ cid: info.cid, accountType: info.accountType, organizationCid: info.organizationCid ?? undefined, roles: info.roles ?? undefined });
                } catch { /* non-fatal */ }
                consent();
            },
            onRegister: hooks.onRegister,
        });
        return;
    }

    // Not logged in — the login form finds where the typed identity lives
    // before it sends anything; an identity of another node is forwarded there.
    renderLoginView(appEl, {
        onSuccess: consent,
        onRegister: hooks.onRegister,
    }, {
        prefillCid: params.loginHint || undefined,
        onHomeNodeElsewhere: (cid) => forward(cid),
    });
}
